"use client";

import { Button } from "@/components/ui/button";
import { HOTMART_CONFIG, WHATSAPP_CONFIG, COMPANY_INFO } from "@/config/constants";
import { CheckCircle, XCircle, Shield, Heart, Target, Brain, Sparkles, RefreshCw, Lock, Users, ChevronDown, Quote, Star, Play } from "lucide-react";
import { useState } from "react";
import logoLDA from "@/assets/logo-horizontal.png";

const Main = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const testimonials = [
    {
      quote: "O método me ajudou a entender meus gatilhos e a organizar melhor minha vida financeira.",
      name: "Maria Clara",
      age: "26 anos",
      state: "PI"
    },
    {
      quote: "Passei a ter mais consciência das minhas decisões e hoje consigo lidar melhor com impulsos.",
      name: "Márcia Soares",
      age: "35 anos",
      state: "ES"
    },
    {
      quote: "O acompanhamento educacional me ajudou a reconstruir minha rotina e recuperar o controle.",
      name: "Jefferson",
      age: "21 anos",
      state: "MG"
    },
    {
      quote: "Aprendi a me responsabilizar pelas minhas escolhas e a criar hábitos mais saudáveis.",
      name: "Patrícia",
      age: "35 anos",
      state: "PE"
    }
  ];

  const pillars = [
    { title: "Consciência de Escolhas", desc: "Entenda como decisões impactam sua vida financeira e emocional.", icon: Brain },
    { title: "Reconhecimento Comportamental", desc: "Aprenda a identificar padrões e gatilhos pessoais.", icon: Target },
    { title: "Motivação Sustentável", desc: "Construa disciplina sem depender de empolgação momentânea.", icon: Sparkles },
    { title: "Estratégias de Autocontrole", desc: "Técnicas práticas para lidar melhor com impulsos.", icon: Shield },
    { title: "Reconstrução de Identidade", desc: "Reorganize valores, prioridades e objetivos pessoais.", icon: Heart },
    { title: "Responsabilidade Pessoal", desc: "Aprenda a assumir controle sobre escolhas e consequências.", icon: Users },
    { title: "Construção de Nova Rotina", desc: "Desenvolva hábitos alinhados a uma vida mais equilibrada.", icon: RefreshCw },
    { title: "Manutenção e Prevenção", desc: "Ferramentas educacionais para manter constância no processo.", icon: Lock }
  ];

  const before = [
    "Falta de controle emocional",
    "Dificuldades financeiras recorrentes",
    "Rotina desorganizada",
    "Conflitos pessoais e familiares"
  ];

  const after = [
    "Maior consciência comportamental",
    "Organização financeira progressiva",
    "Rotina estruturada",
    "Clareza de objetivos pessoais"
  ];

  const faqs = [
    { q: "O Método LDA é terapia?", a: "Não. É um programa educacional." },
    { q: "Funciona para todos?", a: "Os resultados variam conforme o comprometimento individual." },
    { q: "Preciso contar para outras pessoas?", a: "Não. O processo é individual e confidencial." },
    { q: "Existe garantia?", a: "Sim. Garantia legal de 7 dias." }
  ];

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-primary">
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/30 rounded-full blur-[100px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/20 rounded-full blur-[100px] animate-glow-pulse" style={{ animationDelay: "2s" }} />
        </div>

        <div className="container relative z-10 px-4 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8 animate-slide-up">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight">
              Liberte-se das Apostas.
              <br />
              <span className="bg-gradient-accent bg-clip-text text-transparent">
                Viva com Mais Consciência e Controle.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-accent font-semibold">
              Você não está sozinho.
            </p>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Milhares de pessoas enfrentam dificuldades com apostas online todos os dias — e buscar apoio é o primeiro passo para retomar o controle.
            </p>
            
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              O <strong className="text-foreground">Método Libertação das Apostas (LDA)</strong> é um programa educacional e de desenvolvimento pessoal, criado para ajudar pessoas que desejam reduzir comportamentos compulsivos, organizar a vida financeira e reconstruir hábitos mais saudáveis.
            </p>
            
            <p className="text-base text-muted-foreground flex items-start justify-center gap-2">
              <span className="text-xl">👉</span>
              <span>Um método baseado em educação, autoconsciência e estratégias comportamentais, sem promessas irreais.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button 
                variant="hero" 
                size="xl" 
                className="group"
                onClick={() => scrollToSection("pricing")}
              >
                <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Quero Conhecer o Método
              </Button>
              <Button 
                variant="glass" 
                size="xl"
                onClick={() => scrollToSection("testimonials")}
              >
                Ver Depoimentos
              </Button>
            </div>
            
            <div className="flex items-center gap-8 justify-center text-sm pt-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                <span className="text-muted-foreground">+1.200 pessoas já iniciaram</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                <span className="text-muted-foreground">Programa educacional</span>
              </div>
            </div>
            
            <p className="text-sm text-amber-400/80 flex items-center justify-center gap-2 pt-4">
              <span>⚠️</span>
              <span>Este programa não substitui tratamento médico, psicológico ou psiquiátrico.</span>
            </p>
          </div>
        </div>

        {/* Bottom wave decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Social Proof */}
      <section className="py-20 relative">
        <div className="container px-4">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Mais de{" "}
              <span className="bg-gradient-accent bg-clip-text text-transparent">
                1.200 Pessoas
              </span>
              {" "}Já Iniciaram
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Pessoas comuns que decidiram buscar orientação, aprender sobre seus comportamentos e criar uma nova relação com dinheiro, rotina e escolhas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              "Conteúdo educacional",
              "Estratégias práticas",
              "Apoio estruturado",
              "Foco em consciência"
            ].map((item, i) => (
              <div
                key={i}
                className="group relative animate-scale-in"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="relative h-full backdrop-blur-xl bg-card/40 rounded-2xl p-6 border border-primary/20 hover:border-primary/40 transition-all duration-500 hover:shadow-[0_8px_32px_rgba(100,192,242,0.2)] hover:-translate-y-2 text-center">
                  <CheckCircle className="w-10 h-10 text-accent mx-auto mb-4" />
                  <p className="font-semibold">{item}</p>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-muted-foreground italic text-center mt-8">
            Os resultados variam de acordo com o perfil, dedicação e aplicação do conteúdo.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 relative">
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-primary/15 to-transparent pointer-events-none" />
        
        <div className="container px-4 relative z-10">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Relatos de{" "}
              <span className="bg-gradient-accent bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(100,192,242,0.6)]">
                Participantes
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Veja o que dizem pessoas que iniciaram sua jornada de mudança
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="group backdrop-blur-xl bg-card/40 rounded-2xl p-6 border border-primary/20 hover:border-primary/40 transition-all duration-500 hover:shadow-[0_8px_32px_rgba(100,192,242,0.2)] hover:-translate-y-2"
              >
                <Quote className="w-8 h-8 text-accent/30 mb-4" />
                <p className="text-lg italic text-foreground/90 mb-4">"{t.quote}"</p>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-accent fill-accent" />
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    — <strong className="text-foreground">{t.name}</strong>, {t.age} • {t.state}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-center text-muted-foreground mt-8">
            🔹 Relatos reais de participantes. Resultados individuais podem variar.
          </p>
        </div>
      </section>

      {/* Before/After Transformation */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px]" />
        </div>

        <div className="container px-4 relative z-10">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Antes e Depois do{" "}
              <span className="bg-gradient-accent bg-clip-text text-transparent">
                Método LDA
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Compare a jornada de transformação
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* ANTES */}
            <div className="space-y-4 animate-slide-in-right">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-destructive">Antes</h3>
              </div>
              {before.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 backdrop-blur-sm bg-card/20 p-4 rounded-lg border border-destructive/20"
                >
                  <XCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">{item}</p>
                </div>
              ))}
            </div>

            {/* DEPOIS */}
            <div className="space-y-4 animate-slide-in-right" style={{ animationDelay: "0.2s" }}>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-accent">Depois</h3>
              </div>
              {after.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 backdrop-blur-sm bg-card/20 p-4 rounded-lg border border-accent/20"
                >
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What is LDA */}
      <section className="py-20 relative">
        <div className="container px-4">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              O Que é o{" "}
              <span className="bg-gradient-accent bg-clip-text text-transparent">
                Método LDA
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              O LDA é um programa educacional online, estruturado em 8 pilares, com foco em:
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {[
              "Autoconhecimento",
              "Organização financeira",
              "Desenvolvimento de autocontrole",
              "Construção de hábitos saudáveis",
              "Prevenção comportamental"
            ].map((item, i) => (
              <div
                key={i}
                className="backdrop-blur-xl bg-card/40 rounded-2xl p-4 border border-primary/20 text-center"
              >
                <p className="text-sm font-medium">{item}</p>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-amber-400/80 text-center mt-8">
            ❗ Não é terapia, não é tratamento clínico e não garante resultados específicos.
          </p>
        </div>
      </section>

      {/* 8 Pillars */}
      <section className="py-20 relative">
        <div className="container px-4">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Os 8 Pilares do{" "}
              <span className="bg-gradient-accent bg-clip-text text-transparent">
                Método
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {pillars.map((pillar, index) => (
              <div
                key={index}
                className="group relative animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-full backdrop-blur-xl bg-card/40 rounded-2xl p-6 border border-primary/20 hover:border-primary/40 transition-all duration-500 hover:shadow-[0_8px_32px_rgba(100,192,242,0.2)] hover:-translate-y-2">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <pillar.icon className="w-7 h-7 text-accent" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl font-bold text-primary">{index + 1}.</span>
                    <h3 className="text-lg font-bold">{pillar.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm">{pillar.desc}</p>

                  <div className="absolute inset-0 bg-gradient-radial from-primary/10 via-transparent to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative py-20 md:py-28">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 [mask-image:radial-gradient(60%_60%_at_50%_30%,black,transparent)]"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/60 to-background" />
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
              backgroundSize: "36px 36px",
            }}
          />
        </div>

        <div className="container px-4">
          <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              Escolha a Melhor Opção Para Você
            </h2>
            <p className="mt-4 text-muted-foreground">
              Três caminhos, um objetivo: sua jornada de mudança.
            </p>
          </div>

          <div className="grid gap-6 md:gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {/* Método LDA */}
            <div className="group relative overflow-hidden flex flex-col h-full border bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 transition-all duration-300 rounded-2xl border-border/60 hover:shadow-lg">
              <div className="p-6 space-y-6 flex-1 flex flex-col">
                <div>
                  <p className="text-sm text-primary font-semibold">🔹 Método LDA – Versão Digital</p>
                  <p className="text-sm text-muted-foreground mt-1">Programa educacional online.</p>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground line-through">De R$ 199,00</p>
                  <p className="text-3xl font-bold bg-gradient-accent bg-clip-text text-transparent">R$ 39,90</p>
                </div>
                
                <ul className="space-y-2 flex-1">
                  {[
                    "Acesso aos módulos",
                    "Exercícios práticos",
                    "Conteúdo educacional",
                    "Garantia legal de 7 dias"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-accent" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <Button 
                  variant="hero"
                  size="xl"
                  className="w-full"
                  onClick={() => window.open(HOTMART_CONFIG.metodoLda, "_blank")}
                >
                  Começar Agora
                </Button>
              </div>
            </div>

            {/* 8 Pilares - Featured */}
            <div className="group relative overflow-hidden flex flex-col h-full border bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 transition-all duration-300 rounded-2xl border-transparent ring-2 ring-primary/60 shadow-xl shadow-primary/50 md:scale-[1.05]">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 transition-opacity duration-500 opacity-100"
                style={{
                  background: "radial-gradient(800px 220px at 50% 0%, hsl(var(--primary)/0.14), transparent 65%)",
                }}
              />

              <div className="absolute left-0 right-0 top-0 z-10 h-9 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/15 backdrop-blur-sm border-b border-primary/20 flex items-center justify-center gap-2">
                <Star className="h-4 w-4 fill-current" />
                <span className="text-xs font-semibold uppercase tracking-widest">
                  Recomendado
                </span>
                <Star className="h-4 w-4 fill-current" />
              </div>

              <div className="p-6 pt-12 space-y-6 flex-1 flex flex-col relative">
                <div>
                  <p className="text-sm text-primary font-semibold">🔹 Jornada Completa – 8 Pilares</p>
                  <p className="text-sm text-muted-foreground mt-1">Imersão educacional aprofundada.</p>
                </div>
                
                <div>
                  <p className="text-3xl font-bold bg-gradient-accent bg-clip-text text-transparent">R$ 297,00</p>
                  <p className="text-sm text-muted-foreground">ou 12x de R$ 30,72</p>
                </div>
                
                <ul className="space-y-2 flex-1">
                  {[
                    "Conteúdo completo",
                    "Mais de 38 exercícios práticos",
                    "Trilhas organizadas por pilar",
                    "Acompanhamento em grupo"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-accent" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <Button 
                  variant="hero"
                  size="xl"
                  className="w-full"
                  onClick={() => window.open(HOTMART_CONFIG.jornada8Pilares, "_blank")}
                >
                  Quero os 8 Pilares
                </Button>
              </div>
            </div>

            {/* Mentoria */}
            <div className="group relative overflow-hidden flex flex-col h-full border bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 transition-all duration-300 rounded-2xl border-border/60 hover:shadow-lg">
              <div className="p-6 space-y-6 flex-1 flex flex-col">
                <div>
                  <p className="text-sm text-primary font-semibold">🔹 Mentoria Individual</p>
                  <p className="text-sm text-muted-foreground mt-1">Atendimento educacional individualizado.</p>
                </div>
                
                <ul className="space-y-2 flex-1 pt-8">
                  {[
                    "Apoio focado em organização pessoal",
                    "Análise comportamental educacional",
                    "Orientação estruturada"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-accent" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <p className="text-xs text-amber-400/80">
                  ⚠️ Não é acompanhamento psicológico, médico ou terapêutico.
                </p>
                
                <Button 
                  variant="glass"
                  size="xl"
                  className="w-full"
                  onClick={() => window.open(WHATSAPP_CONFIG.supportLink, "_blank")}
                >
                  Falar com a Equipe
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-20 relative">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[120px]" />
        </div>

        <div className="container px-4 relative z-10">
          <div className="max-w-2xl mx-auto text-center backdrop-blur-xl bg-card/40 rounded-3xl p-8 md:p-12 border border-primary/20">
            <Shield className="w-20 h-20 text-accent mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Garantia Legal de 7 Dias
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              Você tem 7 dias para acessar o conteúdo.
            </p>
            <p className="text-muted-foreground mb-4">
              Caso não considere adequado para você, pode solicitar reembolso conforme o Código de Defesa do Consumidor.
            </p>
            <p className="text-accent font-semibold text-xl">Sem burocracia.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 relative">
        <div className="container px-4">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Perguntas{" "}
              <span className="bg-gradient-accent bg-clip-text text-transparent">
                Frequentes
              </span>
            </h2>
          </div>

          <div className="max-w-2xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div 
                key={i} 
                className="backdrop-blur-xl bg-card/40 border border-primary/20 rounded-2xl overflow-hidden hover:border-primary/40 transition-colors"
              >
                <button
                  className="w-full p-5 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-accent transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-muted-foreground">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 border-t border-primary/10">
        <div className="container px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center md:text-left">
                <img src={logoLDA} alt="LDA" className="h-24 mx-auto md:mx-0 mb-4" />
                <p className="text-muted-foreground text-sm">
                  Programa Educacional para Mudança de Hábitos
                </p>
              </div>

              {/* Company Info */}
              <div className="text-center">
                <h4 className="font-semibold mb-4">Informações da Empresa</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>LDA – Libertação das Apostas</li>
                  <li>CNPJ: 53.820.194/0001-38</li>
                  <li>Responsável: Marlon Gustavo da Silva Ribeiro</li>
                </ul>
              </div>

              {/* Contact */}
              <div className="text-center md:text-right">
                <h4 className="font-semibold mb-4">Contato</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>{COMPANY_INFO.email}</li>
                  <li>Telefone/WhatsApp: (11) 5304-1050</li>
                  <li className="mt-3">
                    <strong>Endereço:</strong><br />
                    R. Lira dos Verdes Anos, nº 129<br />
                    Jardim São Savério<br />
                    CEP: 04.194-250<br />
                    São Paulo - SP
                  </li>
                </ul>
              </div>
            </div>

            {/* Legal Disclaimer */}
            <div className="pt-8 border-t border-primary/10 text-center space-y-4">
              <h3 className="font-bold text-lg">Aviso Legal</h3>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>Este site oferece conteúdo educacional e informativo.</p>
                <p>Não substitui diagnóstico, tratamento médico ou psicológico.</p>
                <p>Os resultados podem variar conforme o perfil de cada participante.</p>
              </div>
              <p className="text-xs text-muted-foreground pt-4">
                © {new Date().getFullYear()} LDA – Libertação das Apostas. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Main;
